import { v4 as uuid } from 'uuid';
import { AspectRatio } from '../types.ts/video';
import type { ElementState } from '../renderer/ElementState';
import { deepClone } from './deepClone';
import type { Track } from '../editor/models/Track';
import type { Element } from '../editor/models/Element';

export interface LogoPlacementOptions {
  sourceAspectRatio: number;
  videoAspectRatio: AspectRatio;
  opacity?: string;
  fit?: string;
}

export interface VideoData {
  duration: number;
  time: number;
}

/**
 * Headless-specific video data interface
 */
export interface HeadlessVideoData {
  duration: number;
  time: number;
}

export function combineVideoDataPerTrack(
  tracks: Map<number, ElementState[]>,
  extraFilter: (el: ElementState) => boolean = () => true,
): VideoData[] {
  const result: VideoData[] = [];

  for (const [_track, trackElements] of tracks.entries()) {
    if (
      !trackElements.length ||
      !(trackElements[0].source.type === 'video') ||
      !extraFilter(trackElements[0])
    ) {
      continue;
    }

    const videoElements = deepClone(trackElements);
    videoElements.sort(
      (a, b) => parseFloat(a.source.time) - parseFloat(b.source.time),
    );

    const aTime = parseFloat(videoElements[0].source.time);
    const bTime = parseFloat(
      videoElements[videoElements.length - 1].source.time,
    );
    const bDuration = parseFloat(
      videoElements[videoElements.length - 1].source.duration,
    );

    result.push({
      duration: bTime - aTime + bDuration,
      time: aTime,
    });
  }

  return result;
}

/**
 * Combines video data from headless editor tracks for logo placement
 * @param tracks Array of Track objects from headless editor
 * @param transcriptTrackId Optional transcript track ID to filter by
 * @returns Array of VideoData for logo timing
 */
export function combineVideoDataFromHeadlessTracks(
  tracks: readonly Track[],
  transcriptTrackId?: string,
): VideoData[] {
  const result: VideoData[] = [];

  for (const track of tracks) {
    // Filter by transcript track if specified, otherwise include all tracks
    if (transcriptTrackId && track.id !== transcriptTrackId) {
      continue;
    }

    // Get video elements from this track
    const videoElements = track.elements.filter(
      (element: Element) => element.type === 'video',
    );

    if (!videoElements.length) {
      continue;
    }

    // Sort elements by start time
    const sortedElements = [...videoElements].sort((a, b) => a.time - b.time);

    const firstElement = sortedElements[0];
    const lastElement = sortedElements[sortedElements.length - 1];

    const startTime = firstElement.time;
    const lastElementDuration =
      typeof lastElement.duration === 'number' ? lastElement.duration : 0;
    const endTime = lastElement.time + lastElementDuration;

    result.push({
      duration: endTime - startTime,
      time: startTime,
    });
  }

  return result;
}

/**
 * Calculate logo position based on video aspect ratio
 */
export const calculateLogoPosition = (
  sourceAspectRatio: number,
  videoAspectRatio?: AspectRatio,
) => {
  const base = {
    x_anchor: '100%',
    y_anchor: '0%',
  };

  switch (videoAspectRatio) {
    case AspectRatio.AR_16_9:
      return {
        ...base,
        x: '94%',
        y: '4%',
        width: '12%',
        height: `${(12 / sourceAspectRatio) * (16 / 9)}%`,
      };
    case AspectRatio.AR_9_16:
      return {
        ...base,
        x: '92%',
        y: '5%',
        width: '25%',
        height: `${(25 / sourceAspectRatio) * (9 / 16)}%`,
      };
    case AspectRatio.AR_1_1:
      return {
        ...base,
        x: '95%',
        y: '5%',
        width: '19%',
        height: `${19 / sourceAspectRatio}%`,
      };
    default:
      return {
        ...base,
        x: '94%',
        y: '4%',
        width: '12%',
        height: `${12 / sourceAspectRatio}%`,
      };
  }
};
